import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../firebase/AuthContext';
import { uploadUserIcon } from '../firebase/storage';
import { validateImageFile, getUploadErrorMessage } from '../utils/imageValidation';
import { trackSignup } from '../utils/analytics';
import { waitForPendingWrites } from 'firebase/firestore';
import { db } from '../firebase/config';
import './Regist.css';

const Regist = () => {
  const navigate = useNavigate();
  const { signUp, updateUserProfile } = useAuth();
  const [formData, setFormData] = useState({
    nickname: '',
    email: '',
    password: '',
    gender: '',
    birthYear: '',
    birthMonth: '',
    birthDay: ''
  });
  const [iconPreview, setIconPreview] = useState('/img/icon.png');
  const [iconFile, setIconFile] = useState(null);
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState('');

  // 年の選択肢を生成（現在年-100歳から現在年-13歳まで）
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 100;
    const endYear = currentYear - 13;
    const years = [];
    for (let year = endYear; year >= startYear; year--) {
      years.push(year);
    }
    return years;
  };

  // 月の選択肢を生成
  const generateMonths = () => {
    return Array.from({ length: 12 }, (_, i) => i + 1);
  };

  // 日の選択肢を生成（選択された年月に応じて）
  const generateDays = () => {
    if (!formData.birthYear || !formData.birthMonth) return [];
    const year = parseInt(formData.birthYear);
    const month = parseInt(formData.birthMonth);
    const daysInMonth = new Date(year, month, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      // 年または月が変更された場合、日をリセット
      if ((name === 'birthYear' || name === 'birthMonth') && prev.birthDay) {
        const year = name === 'birthYear' ? parseInt(value) : parseInt(prev.birthYear);
        const month = name === 'birthMonth' ? parseInt(value) : parseInt(prev.birthMonth);
        if (year && month) {
          const daysInMonth = new Date(year, month, 0).getDate();
          if (parseInt(prev.birthDay) > daysInMonth) {
            newData.birthDay = '';
          }
        }
      }
      
      return newData;
    });

    // パスワードバリデーション
    if (name === 'password') {
      validatePassword(value);
    }
  };

  const validatePassword = (password) => {
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const isValidLength = password.length >= 8 && password.length <= 20;
    
    if (password && (!hasLetter || !hasNumber || !isValidLength)) {
      setPasswordError('半角英字・半角数字を1文字ずつ使用してください');
    } else {
      setPasswordError('');
    }
  };

  const handleIconUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // ファイルバリデーション
    const validation = validateImageFile(file, 5 * 1024 * 1024); // 5MB制限
    if (!validation.valid) {
      alert(validation.error);
      e.target.value = ''; // ファイル選択をクリア
      return;
    }

    setIconFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setIconPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // バリデーション
    if (passwordError) {
      setError('パスワードの形式が正しくありません');
      return;
    }

    if (!formData.email || !formData.password) {
      setError('メールアドレスとパスワードは必須です');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // 生年月日のバリデーション
      if (!formData.birthYear || !formData.birthMonth || !formData.birthDay) {
        setError('生年月日を入力してください');
        setLoading(false);
        return;
      }

      // 生年月日を結合してISO形式に変換
      const birthdate = `${formData.birthYear}-${formData.birthMonth.padStart(2, '0')}-${formData.birthDay.padStart(2, '0')}`;

      // Firebase認証でユーザー作成
      const userInfo = {
        nickname: formData.nickname || 'ユーザー',
        gender: formData.gender,
        birthdate: birthdate
      };

      const user = await signUp(formData.email, formData.password, userInfo);
      // 新規登録成功
      trackSignup('email');

      // アイコンがアップロードされている場合、Firebase Storageに保存し、
      // プロフィール（Firestore）のiconURLも更新する（保存だけではUIに反映されないため）
      // ※メール認証待ちの分岐より前に実行しないと、新規登録は必ず未認証状態のため
      //   下のreturnで抜けてしまいアイコンが保存されないまま失われる
      if (iconFile && user) {
        try {
          const uploadResult = await uploadUserIcon(user.uid, iconFile);
          await updateUserProfile(user.uid, { iconURL: uploadResult.url });
          // Firestoreはオフライン対応のため、updateDocはローカルキャッシュへの書き込みが
          // 完了した時点で成功を返す（サーバーへの同期完了は保証しない）。
          // このすぐ後にメール確認のため画面・タブを離れられてしまう可能性があるため、
          // サーバーへの同期が完了するまで明示的に待ってから次に進む
          await waitForPendingWrites(db);
          // アイコンアップロード成功
        } catch (iconError) {
          console.error('アイコンアップロードエラー:', iconError);
          const errorMessage = getUploadErrorMessage(iconError);
          console.warn(`アイコンアップロード失敗: ${errorMessage}`);
          // アイコンアップロードが失敗してもユーザー登録は成功とする
        }
      }

      // メール認証メールを送信
      if (user && !user.emailVerified) {
        const { sendEmailVerification } = await import('firebase/auth');
        await sendEmailVerification(user);
        // 認証メール送信完了

        // メール認証画面に遷移
        navigate('/email-verification', {
          state: {
            email: formData.email,
            from: '/top'
          }
        });
        return;
      }

      // 既にメール認証済みの場合はTop画面に遷移
      navigate('/top');
      
    } catch (error) {
      console.error('新規登録エラー:', error);
      
      // Firebase認証のエラーメッセージを日本語化
      let errorMessage = '新規登録に失敗しました';
      
      if (error.code) {
        switch (error.code) {
          case 'auth/email-already-in-use':
            errorMessage = 'このメールアドレスは既に使用されています';
            break;
          case 'auth/invalid-email':
            errorMessage = 'メールアドレスの形式が正しくありません';
            break;
          case 'auth/weak-password':
            errorMessage = 'パスワードが弱すぎます';
            break;
          case 'auth/operation-not-allowed':
            errorMessage = 'この認証方法は無効です';
            break;
          default:
            errorMessage = error.message || '新規登録に失敗しました';
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToStart = () => {
    navigate('/');
  };

  const openModal = async (url) => {
    try {
      const response = await fetch(url);
      const htmlContent = await response.text();
      setModalContent(htmlContent);
      setShowModal(true);
    } catch (error) {
      console.error('Failed to load content:', error);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalContent('');
  };

  return (
    <div className="regist-page-container">
      <div className="container">
        <div className="sub-container">
          <img src="/img/tiny-identity.png" alt="Tiny identity ロゴ" />
          <h1 className="logo-title">Tiny identity</h1>

        {/* エラーメッセージ - 既存デザインに影響しないインラインスタイル */}
        {error && (
          <div style={{
            color: '#e74c3c',
            backgroundColor: '#fdf2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            padding: '10px',
            marginBottom: '20px',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}

        {/* 登録フォーム本体 */}
        <form className="regist-form" onSubmit={handleSubmit}>
          <div className="form-row nickname-row">
            <div className="icon-upload">
              <label htmlFor="icon-upload-input">
                <img src={iconPreview} alt="アイコン画像" className="icon-preview" />
              </label>
              <input 
                type="file" 
                id="icon-upload-input" 
                accept="image/*" 
                onChange={handleIconUpload}
                disabled={loading}
              />
            </div>
            <div className="nickname-input">
              <label htmlFor="nickname">ニックネーム</label>
              <input 
                type="text" 
                id="nickname" 
                name="nickname" 
                value={formData.nickname}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-row">
            <label htmlFor="email">メールアドレス（必須）</label>
            <input 
              type="email" 
              id="email" 
              name="email" 
              value={formData.email}
              onChange={handleInputChange}
              required
              disabled={loading}
            />
          </div>

          <div className="form-row">
            <label htmlFor="password">パスワード（必須）</label>
            <input 
              type="password" 
              id="password" 
              name="password" 
              value={formData.password}
              onChange={handleInputChange}
              placeholder="半角英数8〜20文字で入力してください" 
              required
              disabled={loading}
            />
            {passwordError && (
              <div style={{
                color: '#e74c3c',
                fontSize: '0.8rem',
                marginTop: '5px',
                marginBottom: '10px'
              }}>
                {passwordError}
              </div>
            )}
          </div>

          <p className="info-message">
            以下は、コンテンツを共有する際参考にさせていただく情報となります
          </p>

          <div className="form-row">
            <label>性別</label>
            <div className="regist-radio-group">
              <label className="regist-radio">
                <input 
                  type="radio" 
                  name="gender" 
                  value="male" 
                  checked={formData.gender === 'male'}
                  onChange={handleInputChange}
                />
                <span>男性</span>
              </label>
              <label className="regist-radio">
                <input 
                  type="radio" 
                  name="gender" 
                  value="female" 
                  checked={formData.gender === 'female'}
                  onChange={handleInputChange}
                />
                <span>女性</span>
              </label>
              <label className="regist-radio">
                <input 
                  type="radio" 
                  name="gender" 
                  value="other" 
                  checked={formData.gender === 'other'}
                  onChange={handleInputChange}
                />
                <span>その他</span>
              </label>
            </div>
          </div>

          <div className="form-row regist-field">
            <label>生年月日</label>
            <div className="birth-date-container">
              <select 
                name="birthYear" 
                value={formData.birthYear}
                onChange={handleInputChange}
                className="birth-select"
              >
                <option value="">年</option>
                {generateYears().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <span className="birth-separator">年</span>
              
              <select 
                name="birthMonth" 
                value={formData.birthMonth}
                onChange={handleInputChange}
                className="birth-select"
              >
                <option value="">月</option>
                {generateMonths().map(month => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
              <span className="birth-separator">月</span>
              
              <select 
                name="birthDay" 
                value={formData.birthDay}
                onChange={handleInputChange}
                className="birth-select"
                disabled={!formData.birthYear || !formData.birthMonth}
              >
                <option value="">日</option>
                {generateDays().map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
              <span className="birth-separator">日</span>
            </div>
          </div>

          <ul className="document-list">
            <li>
              <img src="/img/document.png" alt="質問アイコン" />
              <a href="#" onClick={(e) => { e.preventDefault(); openModal('/terms.html'); }}>利用規約</a>
            </li>
            <li>
              <img src="/img/document.png" alt="質問アイコン" />
              <a href="#" onClick={(e) => { e.preventDefault(); openModal('/privacy.html'); }}>個人情報保護方針</a>
            </li>
          </ul>

          <p className="agreement-text">
            新規登録ボタン押下で利用規約・個人情報保護方針に同意いただいたものとみなします
          </p>

          <button 
            type="submit" 
            className="btn-primary"
            disabled={loading || passwordError}
            style={{
              opacity: (loading || passwordError) ? 0.6 : 1,
              cursor: (loading || passwordError) ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? '登録中...' : '新規登録'}
          </button>

          <button
            type="button"
            className="btn-cancel"
            onClick={handleBackToStart}
            disabled={loading}
            style={{
              opacity: loading ? 0.6 : 1,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            キャンセル
          </button>

          <p className="regist-login-guide">
            アカウントをすでに持っている方はこちら
          </p>
          <button
            type="button"
            className="btn-outline"
            onClick={() => navigate('/login')}
            disabled={loading}
            style={{
              opacity: loading ? 0.6 : 1,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            ログインへ
          </button>
        </form>
      </div>

      {/* モーダル */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              padding: '15px 20px',
              borderBottom: '1px solid #eee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, color: '#333' }}>文書</h3>
              <button
                onClick={closeModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>
            <div 
              style={{
                padding: 0,
                overflow: 'auto',
                flex: 1
              }}
              dangerouslySetInnerHTML={{ __html: modalContent }}
            />
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Regist;
